import {Component, HostBinding, inject, input, computed, effect, signal, untracked} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatListModule} from '@angular/material/list';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatBadgeModule} from '@angular/material/badge';
import {MatTooltipModule} from '@angular/material/tooltip';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {AuthService} from '@on-the-go-web/helpers/services/auth.service';
import {filter, map, startWith} from 'rxjs';
import {
  LocationsDataService,
  CopilotService,
} from '@on-the-go-web/library/helpers';
import {ENABLE_PREVIEW_FEATURES_TOKEN} from '@on-the-go-web/library';
import {TranslatePipe} from '@ngx-translate/core';
import {SidenavService} from '../../helpers/services/sidenav.service';
import {BreakpointObserver} from '@angular/cdk/layout';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatMenuModule} from '@angular/material/menu';
import {
  LocationPickerDialog,
  LocationPickerDialogData,
} from '../location-picker-dialog/location-picker-dialog';
import {MatDialog} from '@angular/material/dialog';

// How long the co-pilot takes to grow into full page and back (see the scss).
const COPILOT_RESIZE_MS = 400;

@Component({
  selector: 'otg-sidenav',
  imports: [
    CommonModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatBadgeModule,
    MatTooltipModule,
    MatMenuModule,
    MatButtonModule,
    RouterModule,
    TranslatePipe,
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
})
export class SidenavComponent {
  readonly logoPath = input<string>();
  @HostBinding('style.--sidenav-width') get sidenavWidth() {
    return this.open() ? '320px' : this.isMobile() ? '0px' : '82px';
  }
  @HostBinding('class.logged-in') get loggedIn() {
    return this.isLoggedIn();
  }

  public authService = inject(AuthService);
  private _locationData = inject(LocationsDataService);
  _sidenavService = inject(SidenavService);
  private _breakpointObserver = inject(BreakpointObserver);
  private _router = inject(Router);
  private _dialog = inject(MatDialog);
  private _locationsDataService = inject(LocationsDataService);

  currentLocation = this._locationsDataService.currentLocation;

  open = this._sidenavService.isOpen;

  isMobile = toSignal(
    this._breakpointObserver.observe('(max-width: 599.98px)').pipe(map((result) => result.matches)),
    {initialValue: false},
  );

  isLoggedIn = toSignal(this.authService.loggedIn$, {initialValue: false});

  drawerMode = computed(() => (this.isMobile() ? 'over' : 'side'));

  protected readonly copilot = inject(CopilotService);

  // Side-by-side when there's room for the page next to the drawer; narrower
  // screens overlay it (with a backdrop, focus trap and Escape).
  copilotMode = this.copilot.mode;

  // The co-pilot covers the page while full page, and still while it shrinks
  // back (until its width transition ends): side by side straight away, the
  // page would first be squeezed out by the still-wide drawer.
  readonly copilotCovers = signal(false);
  private _reducedMotion = toSignal(
    this._breakpointObserver
      .observe('(prefers-reduced-motion: reduce)')
      .pipe(map((result) => result.matches)),
    {initialValue: false},
  );
  private _copilotCovers = effect((onCleanup) => {
    if (this.copilot.expanded()) {
      this.copilotCovers.set(true);
    } else if (this._reducedMotion()) {
      // Without the width transition, there's nothing to wait for.
      this.copilotCovers.set(false);
    } else if (untracked(this.copilotCovers)) {
      // Nor when its width doesn't change (e.g. full page on a phone, which
      // is as wide as the side panel there): no transition ends then.
      const shrunk = setTimeout(() => this.copilotCovers.set(false), COPILOT_RESIZE_MS + 100);
      onCleanup(() => clearTimeout(shrunk));
    }
  });

  // Done (or cut short) growing or shrinking.
  copilotResized(event: TransitionEvent): void {
    // Not the transitions of what's inside it, which bubble up.
    if (event.target !== event.currentTarget || event.propertyName !== 'width') return;
    if (!this.copilot.expanded()) this.copilotCovers.set(false);
  }

  // Slid out: it opens as the side panel again, also when it had no width to
  // shrink back from (e.g. full page, then made phone-sized).
  copilotClosed(): void {
    this.copilot.closed();
    this.copilotCovers.set(false);
  }

  locations$ = this._locationData.locations$;

  // Inbox and Publish are preview features; without them they show "Coming soon".
  private readonly _previewEnabled = inject(ENABLE_PREVIEW_FEATURES_TOKEN);

  // Analytics/Inbox/Publish/Data are location-scoped — link into the
  // current (or, failing that, most-recently-used) location, or to the
  // picker if none has ever been selected.
  protected readonly items = [
    {icon: 'trending_up', labelKey: 'analytics', section: 'analytics', enabled: true},
    {icon: 'inbox', labelKey: 'inbox', section: 'inbox', enabled: this._previewEnabled},
    {icon: 'publish', labelKey: 'publish', section: 'publish', enabled: this._previewEnabled},
  ];

  // routerLinkActive can't tell an item is active here: the link points at
  // /l/current/<section>, but the router redirects that to /l/<locationId>/<section>
  // before it ever becomes the current URL, so their URL trees never match.
  // Compare the resolved section segment instead.
  private _urlSection = () => this._router.url.split('?')[0].split('/')[3];

  protected readonly activeSection = toSignal(
    this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this._urlSection()),
      startWith(this._urlSection()),
    ),
    {initialValue: this._urlSection()},
  );

  private _failedLogoUrl = signal<string | null>(null);

  showLogo = computed(() => {
    const logoUrl = this.currentLocation()?.logo?.url;
    return !!logoUrl && logoUrl !== this._failedLogoUrl();
  });

  onLogoError(): void {
    const logoUrl = this.currentLocation()?.logo?.url;
    if (logoUrl) {
      this._failedLogoUrl.set(logoUrl);
    }
  }

  toggleSidenav() {
    this._sidenavService.toggle();
  }

  openPicker(): void {
    const dialogRef = this._dialog.open<LocationPickerDialog, LocationPickerDialogData, string>(
      LocationPickerDialog,
      {
        data: {
          locations: this._locationsDataService.locations() ?? [],
          currentLocationId: this._locationsDataService.currentLocationId() ?? null,
        },
        width: '100%',
        maxWidth: '425px',
      },
    );

    dialogRef.afterClosed().subscribe((locationId) => {
      if (locationId) {
        this._router.navigate(['/l', locationId, 'data']);
      }
    });
  }
}
